import { CTSnapshot, CertificateSnapshot, DNSSnapshot, HTTPSnapshot, SnapshotType } from '@analyzer/client';
import {
  AppConsoleMessage,
  EthereumTransaction,
  FrameData,
  WalletInteraction,
  WebSocketRequest
} from '@analyzer/core/lib/collector/collectors/web.js';
import { LiquityEvent } from '@analyzer/core/lib/collector/plugins/liquity/index.js';
import { WalletProviderEvent } from '@analyzer/core/lib/collector/plugins/wallet-provider/index.js';
import { HTTPTransaction } from '@analyzer/core/lib/domain/models/snapshot-web.js';
import { WalletProviderAlert } from '../components/alert/WalletProviderAlert.js';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CodeIcon from '@mui/icons-material/Code';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Link as MuiLink,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import * as x509 from '@peculiar/x509';
import { JsonViewer } from '@textea/json-viewer';
import { useSnackbar } from 'notistack';
import prettyBytes from 'pretty-bytes';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { useSnapshotReadQuery, useWebSnapshotArchiveMutation } from '../api/snapshot.js';
import SummarizedLink from '../components/url.js';
import { asPEM, handleDownload, formatDateUTC } from '../components/utils.js';
import { useService } from '../providers/ServiceProvider.js';
import { CreateSnapshotAnalysisDialog } from './AnalysisView.js';

function formatSnapshotDate(value: string | number | Date) {
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short'
  }).format(new Date(value));
}

function getFilenameFromUrl(url: string, defaultFilename = 'default.txt') {
  const { pathname } = new URL(url);
  const lastSegment = pathname.split('/').pop();
  return lastSegment && lastSegment.length ? lastSegment : defaultFilename;
}

function WebRequestView({ frames, webRequests }: { frames: FrameData[]; webRequests: HTTPTransaction[] }) {
  const client = useService();
  const [openJsonModal, setOpenJsonModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<HTTPTransaction | null>(null);

  const download = async (webRequest: HTTPTransaction) => {
    const filename = getFilenameFromUrl(webRequest.request_url);
    const contents = await client.readResponseBody(webRequest.response_sha256);
    handleDownload(contents, filename);
  };

  const downloadRequestBody = async (webRequest: HTTPTransaction) => {
    if (!webRequest.request_postData) return;
    const method = webRequest.request_method?.toLowerCase() ?? 'request';
    const filename = `${method}-request-body.bin`;
    const contents = await client.readRequestBody(webRequest.request_postData);
    handleDownload(contents, filename);
  };

  // Handler to open the JSON modal
  const handleViewJsonClick = (transaction: HTTPTransaction) => {
    const transactionForJsonView = { ...transaction };
    setSelectedTransaction(transactionForJsonView);
    setOpenJsonModal(true);
  };

  const handleCloseJsonModal = () => {
    setOpenJsonModal(false);
    setSelectedTransaction(null);
  };

  const methodPriority = (method: string): number => {
    switch (method.toUpperCase()) {
      case 'GET':
        return 0;
      case 'POST':
        return 1;
      case 'PUT':
        return 2;
      case 'PATCH':
        return 3;
      case 'DELETE':
        return 4;
      default:
        return 5;
    }
  };

  const contentTypePriority = (contentType: string): number => {
    const type = contentType.split(';')[0]?.split('/')[1]?.toLowerCase();

    switch (type) {
      case 'html':
        return 0;
      case 'javascript':
        return 1;
      case 'json':
        return 2;
      default:
        return 3;
    }
  };

  const statusPriority = (status: number): number => {
    return status >= 200 && status < 300 ? 1 : 0;
  };

  const sortedWebRequests = webRequests.slice().sort((a, b) => {
    const methodDiff = methodPriority(a.request_method) - methodPriority(b.request_method);
    if (methodDiff !== 0) return methodDiff;

    const typeDiff =
      contentTypePriority(a.response_contentType) - contentTypePriority(b.response_contentType);
    if (typeDiff !== 0) return typeDiff;

    const statusDiff = statusPriority(a.response_status) - statusPriority(b.response_status);
    if (statusDiff !== 0) return statusDiff;

    return a.request_url.localeCompare(b.request_url);
  });

  const getMethodColor = (method: string = '') => {
    switch (method.toUpperCase()) {
      case 'GET':
        return 'success';
      case 'POST':
        return 'primary';
      case 'PUT':
        return 'warning';
      case 'PATCH':
        return 'secondary';
      case 'DELETE':
        return 'error';
      default:
        return 'default';
    }
  };

  const getContentTypeColor = (contentType: string) => {
    const type = contentType.split(';')[0]?.split('/')[1]?.toLowerCase();
    switch (type) {
      case 'html':
        return 'primary';
      case 'json':
        return 'info';
      case 'javascript':
      case 'css':
        return 'secondary';
      case 'png':
      case 'jpeg':
      case 'jpg':
      case 'svg+xml':
      case 'webp':
        return 'success';
      case 'pdf':
      case 'octet-stream':
        return 'warning';
      case 'xml':
        return 'info';
      case 'woff':
      case 'woff2':
      case 'ttf':
      case 'otf':
      case 'font':
        return 'default';
      default:
        return 'error';
    }
  };

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'success';
    if (status >= 300 && status < 400) return 'info';
    if (status >= 400 && status < 500) return 'warning';
    return 'error';
  };

  return (
    <Box>
      <Typography variant="h6">Frames</Typography>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Page</TableCell>
              <TableCell>Frame</TableCell>
              <TableCell>URL</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {frames
              .sort((a, b) => a.id - b.id)
              .map((frame) => (
                <TableRow key={frame.id}>
                  <TableCell>{frame.page}</TableCell>
                  <TableCell>{frame.id}</TableCell>
                  <TableCell>
                    <SummarizedLink url={frame.url} />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="h6" sx={{ mt: 2 }}>
        HTTP Requests
      </Typography>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Method</TableCell>
              <TableCell>URL</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Actions</TableCell>
              <TableCell>SHA</TableCell>
              <TableCell>Page</TableCell>
              <TableCell>Frame</TableCell>
              <TableCell>Source</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedWebRequests.map((webRequest, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Chip
                    size="small"
                    variant="filled"
                    label={webRequest.request_method}
                    color={getMethodColor(webRequest.request_method)}
                  />
                </TableCell>
                <TableCell>
                  <SummarizedLink url={webRequest.request_url} />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="filled"
                    label={webRequest.response_status}
                    color={getStatusColor(webRequest.response_status)}
                  />
                </TableCell>
                <TableCell>{prettyBytes(webRequest.response_size)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="filled"
                    label={webRequest.response_contentType.split(';')[0]?.split('/')[1]}
                    color={getContentTypeColor(webRequest.response_contentType)}
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row">
                    <Tooltip title="Download Response Body">
                      <IconButton onClick={() => download(webRequest)} size="small">
                        <FileDownloadOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                    {webRequest.request_postData ? (
                      <Tooltip title="Download Request Body">
                        <IconButton onClick={() => downloadRequestBody(webRequest)} size="small">
                          <FileDownloadIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    <Tooltip title="View JSON">
                      <IconButton onClick={() => handleViewJsonClick(webRequest)} size="small">
                        <CodeIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell>{webRequest.response_sha256.slice(0, 8)}...</TableCell>
                <TableCell>{webRequest.page ?? 'N/A'}</TableCell>
                <TableCell>{webRequest.frame ?? 'N/A'}</TableCell>
                <TableCell>{webRequest.source ?? 'collector'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={openJsonModal} onClose={handleCloseJsonModal} maxWidth="md" fullWidth>
        <DialogTitle>
          Transaction Details
          <IconButton
            aria-label="close"
            onClick={handleCloseJsonModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            {/* <CloseIcon /> */}
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTransaction ? (
            <JsonViewer value={selectedTransaction} style={{ minHeight: '300px' }} />
          ) : (
            <Typography>No transaction selected.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function WebSocketView({ sockets }: { sockets: WebSocketRequest[] }) {
  return (
    <Box>
      <Typography variant="h6">Web Socket Requests</Typography>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>URL</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sockets.map((socket, index) => (
              <TableRow key={index}>
                <TableCell>{index}</TableCell>
                <TableCell>
                  <SummarizedLink url={socket.url} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function EthereumAddress({ address }: { address: string }) {
  return <SummarizedLink url={`https://etherscan.io/address/${address}`} title={address} />;
}

function EthereumBlock({ block }: { block: string }) {
  const n = parseInt(block, 16);
  return <SummarizedLink url={`https://etherscan.io/block/${n}`} title={n.toString()} />;
}

function EthereumChain({ chainId }: { chainId: string }) {
  switch (parseInt(chainId, 16)) {
    case 1:
      return <>Mainnet</>;
    case 3:
      return <>Ropsten</>;
    case 4:
      return <>Rinkeby</>;
    case 42:
      return <>Kovan</>;
    case 5:
      return <>Görli</>;
    case 100:
      return <>xDai</>;
    default:
      return <>Unknown</>;
  }
}

function EthereumMethod({ method }: { method: string }) {
  if (method === 'eth_requestAccounts') return <>eth_requestAccounts</>;

  return (
    <SummarizedLink
      url={`https://docs.infura.io/infura/networks/ethereum/json-rpc-methods/${method.toLowerCase()}`}
      title={method}
    />
  );
}

function EthereumTransactionResponse({ transaction }: { transaction: EthereumTransaction }) {
  switch (transaction.method) {
    case 'eth_accounts':
    case 'eth_requestAccounts':
      return transaction.response.map((address: string) => <EthereumAddress address={address} />);

    case 'eth_blockNumber':
      return <EthereumBlock block={transaction.response} />;

    case 'eth_chainId':
      return <EthereumChain chainId={transaction.response} />;

    default:
      return <pre>{JSON.stringify(transaction.response)}</pre>;
  }
}

function EthereumTransactionView({ transactions }: { transactions: EthereumTransaction[] }) {
  return (
    <Box>
      <Typography variant="h6">Ethereum RPC Requests</Typography>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Method</TableCell>
              <TableCell>Params</TableCell>
              <TableCell>Response</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((tx, index) => (
              <TableRow key={index}>
                <TableCell>
                  <EthereumMethod method={tx.method} />
                </TableCell>
                <TableCell>
                  <pre>{JSON.stringify(tx.params, null, 2)}</pre>
                </TableCell>
                <TableCell>
                  <EthereumTransactionResponse transaction={tx} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function MonitoredAPIView({ events }: { events: WalletProviderEvent[] }) {
  if (events.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No monitored API calls detected in this snapshot.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {events.map((event, idx) => (
        <WalletProviderAlert
          key={idx}
          alert={{
            type: 'monitored-api',
            severity: 'info',
            data: event
          }}
        />
      ))}
    </Stack>
  );
}

function ConsoleMessageView({ messages }: { messages: AppConsoleMessage[] }) {
  return (
    <Box>
      <Typography variant="h6">Console Messages</Typography>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {messages.map((message, index) => (
              <TableRow key={index}>
                <TableCell>{message.type}</TableCell>
                <TableCell>
                  <SummarizedLink url={message.location} />
                </TableCell>
                <TableCell>
                  <pre>{message.text}</pre>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function LiquityView({ liquityEvents }: { liquityEvents: LiquityEvent[] }) {
  const [liquity] = liquityEvents;
  if (!liquity)
    return (
      <>
        <Box>
          <Typography variant="h6">No Liquity Events</Typography>
        </Box>
      </>
    );

  return (
    <Box>
      <Typography variant="h6">Liquity Events</Typography>
      <Paper sx={{ mt: 2 }}>
        <List dense={true}>
          <ListSubheader>Metadata</ListSubheader>
          <ListItem>
            <ListItemText primary="Chain ID" secondary={liquity.chainId} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Deployment Date" secondary={liquity.deploymentDate.toString()} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Frontend Tag" secondary={liquity.frontendTag} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Start Block" secondary={liquity.startBlock} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Version" secondary={liquity.version} />
          </ListItem>
          <ListSubheader>Addresses</ListSubheader>
          {Object.entries(liquity.addresses).map(([key, value]) => (
            <ListItem key={key}>
              <ListItemText primary={`Address for '${key}'`} secondary={value} />
            </ListItem>
          ))}
        </List>
      </Paper>
    </Box>
  );
}

function WalletInteractionView({ interactions }: { interactions: WalletInteraction[] }) {
  return (
    <Box>
      <Typography variant="h6">Wallet Interactions</Typography>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Method</TableCell>
              <TableCell>Params</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {interactions.map((interaction, index) => (
              <TableRow key={index}>
                <TableCell>{interaction.method}</TableCell>
                <TableCell>
                  <pre>{JSON.stringify(interaction.params)}</pre>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function ScreenshotView({ screenshotId }: { screenshotId?: string | null }) {
  const client = useService();
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!screenshotId) return;

    const controller = new AbortController();
    let objectUrl: string | null = null;

    setSrc(null);
    setError(null);

    void (async () => {
      try {
        const blob = await client.readScreenshot(screenshotId, { signal: controller.signal });
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        setError(err?.message ?? 'Failed to load screenshot');
      }
    })();

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [client, screenshotId]);

  if (!screenshotId) return null;

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 1 }}>
        {error}
      </Alert>
    );
  }

  if (!src) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 80 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return <Box component="img" src={src} alt="screenshot" sx={{ borderRadius: 1, maxWidth: '100%' }} />;
}

function WebSnapshotView({ snapshot }: { snapshot: HTTPSnapshot }) {
  const client = useService();
  const [value, setValue] = useState('1');
  const [openDialog, setOpenDialog] = useState(false);
  const [archiveUrl, setArchiveUrl] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const archiveMutation = useWebSnapshotArchiveMutation();

  const handleArchiveClick = () => {
    archiveMutation.mutate(
      { projectId: snapshot.project.id, snapshotId: snapshot.id },
      {
        onSuccess: ({ url }) => {
          setArchiveUrl(url);
          enqueueSnackbar('Incident archived', {
            variant: 'success',
            persist: true,
            action: () => (
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => {
                    void navigator.clipboard.writeText(url);
                    enqueueSnackbar('URL copied', { variant: 'info' });
                  }}
                >
                  Copy URL
                </Button>
                <Button
                  size="small"
                  color="inherit"
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open
                </Button>
              </Stack>
            )
          });
        },
        onError: (err) => {
          enqueueSnackbar(`Archive failed: ${err.message}`, { variant: 'error', persist: true });
        }
      }
    );
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  function chip(name: string, content: number) {
    const contentString = content > 99 ? 'n' : content.toString();
    return <Chip size="small" label={name} avatar={<Avatar>{contentString}</Avatar>} />;
  }

  const handleDownloadHarClick = async () => {
    if (!snapshot.trace) return;

    try {
      const filename = `${snapshot.id}-trace.har`;
      const blob = await client.readHar(snapshot.trace);
      handleDownload(blob, filename, 'application/json');
    } catch (err: any) {
      enqueueSnackbar(err?.message ?? 'Failed to download HAR file', { variant: 'error' });
    }
  };

  // Extract monitored API events from snapshot.data
  const walletInteractionEvents = ((snapshot.data as any[] | null) ?? []).filter(
    (item): item is WalletProviderEvent =>
      typeof item === 'object' && item !== null && 'type' in item && item.type === 'monitored-api'
  );
  const walletInteractionCallCount = walletInteractionEvents.reduce(
    (sum, event) => sum + (event.calls ? event.calls.length : 0),
    0
  );

  return (
    <Stack spacing={2}>
      {/* Header */}
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4">Web Snapshot</Typography>
            <Breadcrumbs aria-label="breadcrumbs" separator="/" sx={{ mt: 0.5 }}>
              <MuiLink component={RouterLink} to="/projects" underline="hover">
                Projects
              </MuiLink>
              <MuiLink component={RouterLink} to={`/projects/${snapshot.project.id}`} underline="hover">
                {snapshot.project.id}
              </MuiLink>

              <MuiLink component={RouterLink} to={`/projects/${snapshot.project.id}/tests`} underline="hover">
                Tests
              </MuiLink>
              <MuiLink
                component={RouterLink}
                to={`/projects/${snapshot.project.id}/tests/${snapshot.test.id}`}
                underline="hover"
              >
                {snapshot.test.id}
              </MuiLink>
              <MuiLink
                component={RouterLink}
                to={`/projects/${snapshot.project.id}/tests/${snapshot.test.id}`}
                underline="hover"
              >
                Snapshots
              </MuiLink>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <IconButton
                  size="small"
                  disabled={!(snapshot as any).prevId}
                  component={RouterLink}
                  to={`/projects/${snapshot.project.id}/snapshots/web/${(snapshot as any).prevId}`}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography color="text.secondary">{snapshot.id}</Typography>
                <IconButton
                  size="small"
                  disabled={!(snapshot as any).nextId}
                  component={RouterLink}
                  to={`/projects/${snapshot.project.id}/snapshots/web/${(snapshot as any).nextId}`}
                >
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Breadcrumbs>
          </Box>

          {/* Buttons aligned to the right */}
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" color="primary" onClick={() => setOpenDialog(true)}>
              Analyze
            </Button>
            <Button
              variant="outlined"
              color="primary"
              component={RouterLink}
              to={
                snapshot.analyses.length > 0
                  ? `/projects/${snapshot.project.id}/analyses/web/${snapshot.analyses[0].id}`
                  : ''
              }
              disabled={snapshot.analyses.length === 0}
            >
              View Analysis
            </Button>
            <Button
              variant="outlined"
              color="primary"
              disabled={!snapshot?.trace}
              onClick={async () => {
                await handleDownloadHarClick();
              }}
            >
              Download HAR File
            </Button>
            {archiveUrl ? (
              <Button
                variant="outlined"
                color="primary"
                component="a"
                href={archiveUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Bundle
              </Button>
            ) : (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleArchiveClick}
                disabled={archiveMutation.isLoading}
              >
                {archiveMutation.isLoading ? 'Archiving…' : 'Archive Incident'}
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      <Box>
        <Typography variant="h5" gutterBottom>
          Metadata
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          divider={<Divider flexItem orientation="vertical" />}
        >
          <Stack spacing={0.25}>
            <Typography variant="overline" color="text.secondary">
              Test
            </Typography>
            <MuiLink
              component={RouterLink}
              to={`/projects/${snapshot.project.id}/tests/${snapshot.test.id}`}
              underline="hover"
            >
              {snapshot.test.target}
            </MuiLink>
          </Stack>

          <Stack spacing={0.25}>
            <Typography variant="overline" color="text.secondary">
              Created
            </Typography>
            <Typography variant="body1">{formatDateUTC(snapshot.createdDate)}</Typography>
          </Stack>

          <Stack spacing={0.25}>
            <Typography variant="overline" color="text.secondary">
              Updated
            </Typography>
            <Typography variant="body1">{formatDateUTC(snapshot.updatedDate)}</Typography>
          </Stack>

          <Stack spacing={0.25}>
            <Typography variant="overline" color="text.secondary">
              Vantage Point
            </Typography>
            <Typography variant="body1">{snapshot.node?.address}</Typography>
          </Stack>

          <Stack spacing={0.25}>
            <Typography variant="overline" color="text.secondary">
              Job
            </Typography>
            <Typography variant="body1">{JSON.stringify(snapshot.job)}</Typography>
          </Stack>

          <Stack spacing={0.25}>
            <Typography variant="overline" color="text.secondary">
              Status
            </Typography>
            <Typography variant="body1" sx={{ color: snapshot.error ? 'error.main' : 'success.main' }}>
              {snapshot.error ? `Error: ${snapshot.error}` : 'Success'}
            </Typography>
          </Stack>
        </Stack>

        <TabContext value={value}>
          <TabList onChange={handleChange}>
            <Tab label={chip('Requests', snapshot.requests.length)} value="1" />
            <Tab label={chip('Sockets', (snapshot.sockets ?? []).length)} value="2" />
            <Tab label={chip('Transactions', (snapshot.transactions ?? []).length)} value="3" />
            <Tab label={chip('Wallet', (snapshot.wallet ?? []).length)} value="4" />
            <Tab label={chip('Console', (snapshot.console ?? []).length)} value="5" />
            <Tab label={chip('Browser APIs', walletInteractionCallCount)} value="9" />
            <Tab label={chip('Screenshots', 1)} value="7" />
            <Tab label={chip('Raw JSON', 1)} value="8" />
          </TabList>
          <TabPanel value="1">
            <WebRequestView frames={snapshot.frames || []} webRequests={snapshot.requests} />
          </TabPanel>
          <TabPanel value="2">
            <WebSocketView sockets={snapshot.sockets ?? []} />
          </TabPanel>
          <TabPanel value="3">
            <EthereumTransactionView transactions={snapshot.transactions ?? []} />
          </TabPanel>
          <TabPanel value="4">
            <WalletInteractionView interactions={snapshot.wallet ?? []} />
          </TabPanel>
          <TabPanel value="5">
            <ConsoleMessageView messages={snapshot.console ?? []} />
          </TabPanel>
          <TabPanel value="7">
            <ScreenshotView screenshotId={snapshot.screenshot} />
          </TabPanel>
          <TabPanel value="9">
            <MonitoredAPIView events={walletInteractionEvents} />
          </TabPanel>
          <TabPanel value="8">
            <Box>
              <Typography variant="h6">Raw Snapshot Data</Typography>
              <JsonViewer value={snapshot} indentWidth={4} />
            </Box>
          </TabPanel>
        </TabContext>
        <CreateSnapshotAnalysisDialog
          open={openDialog}
          onClose={() => {
            setOpenDialog(false);
          }}
          onConfirm={(job) => {
            enqueueSnackbar(`Successfully started analysis job #${job.id}`, { variant: 'success' });
          }}
          onError={(error) => {
            const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
            enqueueSnackbar(`Failed to start analysis: ${errorMessage}`, { variant: 'error' });
          }}
          projectId={snapshot.project.id}
          snapshotId={snapshot.id}
          snapshotType="web"
        />
      </Box>
    </Stack>
  );
}

function TransparencySnapshotView({ snapshot }: { snapshot: CTSnapshot }) {
  const records = snapshot.records.sort((a, b) => {
    const dateA = new Date(a.not_before);
    const dateB = new Date(b.not_before);
    return dateB.getTime() - dateA.getTime();
  });

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h6">Transparency Log Snapshot</Typography>
      <Typography>{`Vantage Point: ${snapshot.node?.address ?? 'N/A'}`}</Typography>
      <Typography>Job: {JSON.stringify(snapshot.job) ?? 'N/A'}</Typography>
      <Typography>
        Root:
        <RouterLink to={`/projects/${snapshot.project.id}/roots/${snapshot.root.id}`}>
          {snapshot.root.hostname}
        </RouterLink>
      </Typography>
      <Typography>Created: {formatSnapshotDate(snapshot.createdDate)}</Typography>
      <Typography>Updated: {formatSnapshotDate(snapshot.updatedDate)}</Typography>
      <Typography>Records:</Typography>
      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Serial</TableCell>
              <TableCell>CN</TableCell>
              <TableCell>Valid From</TableCell>
              <TableCell>Valid To</TableCell>
              <TableCell>Issuer</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((record, index) => (
              <TableRow key={index}>
                <TableCell>{record.serial_number}</TableCell>
                <TableCell>{record.name_value}</TableCell>
                <TableCell>{formatSnapshotDate(record.not_before)}</TableCell>
                <TableCell>{formatSnapshotDate(record.not_after)}</TableCell>
                <TableCell>{record.issuer_name}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function CertificateSnapshotView({ snapshot }: { snapshot: CertificateSnapshot }) {
  const certificate = useMemo(() => new x509.X509Certificate(snapshot.certificate), [snapshot.certificate]);

  const download = () => {
    const filename = `${certificate.serialNumber}.pem`;
    const pem = asPEM(snapshot.certificate);
    handleDownload(pem, filename);
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h6">Certificate</Typography>
      <Typography>{`Vantage Point: ${snapshot.node?.address ?? 'N/A'}`}</Typography>
      <Typography>Job: {JSON.stringify(snapshot.job) ?? 'N/A'}</Typography>
      <Button variant="contained" size="small" onClick={download}>
        Download
      </Button>

      <Typography>
        Resource:
        <RouterLink to={`/projects/${snapshot.project.id}/assets/${snapshot.resource.id}`}>
          {`${snapshot.resource.hostname} @ ${snapshot.resource.address}:${snapshot.resource.port}`}
        </RouterLink>
      </Typography>
      <Typography>Created: {formatSnapshotDate(snapshot.createdDate)}</Typography>
      <Typography>Updated: {formatSnapshotDate(snapshot.updatedDate)}</Typography>
      <Typography>Serial Number: {certificate.serialNumber}</Typography>
      <Typography>Subject: {certificate.subject}</Typography>
      <Typography>Issuer: {certificate.issuer}</Typography>
      <Typography>
        Algorithm: {certificate.signatureAlgorithm.name} {certificate.signatureAlgorithm.hash.name}
      </Typography>
      <Typography>Valid From: {formatSnapshotDate(certificate.notBefore)}</Typography>
      <Typography>Valid Until: {formatSnapshotDate(certificate.notAfter)}</Typography>
    </Box>
  );
}

function DNSSnapshotView({ snapshot }: { snapshot: DNSSnapshot }) {
  return (
    <Paper elevation={1} sx={{ padding: 2 }}>
      <Typography variant="h6">DNS Snapshot</Typography>
      <Typography>{`Vantage Point: ${snapshot.node?.address ?? 'N/A'}`}</Typography>
      <Typography>Job: {JSON.stringify(snapshot.job) ?? 'N/A'}</Typography>
      <Typography>
        Domain:
        <RouterLink to={`/projects/${snapshot.project.id}/domains/${snapshot.domain.id}`}>
          {snapshot.domain.name}
        </RouterLink>
      </Typography>
      <Typography>Created: {formatSnapshotDate(snapshot.createdDate)}</Typography>
      <Typography>Updated: {formatSnapshotDate(snapshot.updatedDate)}</Typography>
      <Typography>Records:</Typography>
      <TableContainer>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Entry</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {snapshot.records.map((record, index) => (
              <TableRow key={index}>
                <TableCell>{record.type}</TableCell>
                <TableCell>{record.entry}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export function SnapshotView() {
  const { projectId, snapshotId, snapshotType } = useParams<{
    projectId: string;
    snapshotId: string;
    snapshotType: SnapshotType;
  }>();

  if (!projectId || !snapshotId || !snapshotType) throw new Error('Missing parameter');

  const {
    data: snapshot,
    isLoading,
    isError,
    error
  } = useSnapshotReadQuery(snapshotType, projectId, snapshotId);

  if (isLoading) return <CircularProgress />;
  if (isError) return <Alert severity="error">{`Error: ${error.message}`}</Alert>;

  switch (snapshotType) {
    case 'web':
      return <WebSnapshotView snapshot={snapshot as HTTPSnapshot} />;
    case 'certificate':
      return <CertificateSnapshotView snapshot={snapshot as CertificateSnapshot} />;
    case 'transparency':
      return <TransparencySnapshotView snapshot={snapshot as CTSnapshot} />;
    case 'dns':
      return <DNSSnapshotView snapshot={snapshot as DNSSnapshot} />;
  }
}
